/**
 * Global error handler hook using Sonner for toast notifications.
 * Provides consistent error and success message display across the application.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { getErrorMessage, extractErrorCode } from '../utils/errorHandler';

/**
 * Hook for displaying error and success messages using toast notifications.
 * Automatically extracts and formats error messages from API errors.
 *
 * @returns Object with showError, showSuccess, and showWarning functions
 */
export function useErrorHandler() {
  /**
   * Display an error toast with extracted error message.
   * Automatically parses Axios errors and backend error responses.
   *
   * @param error - The error to display (any type)
   * @param fallbackMessage - Optional fallback message if error cannot be parsed
   */
  const showError = useCallback((error: unknown, fallbackMessage?: string) => {
    const message = getErrorMessage(error, fallbackMessage);
    const errorCode = extractErrorCode(error);

    // Log error code for debugging if available
    if (errorCode) {
      console.error(`Error [${errorCode}]:`, message);
    }

    toast.error(message, {
      duration: 5000,
    });
  }, []);

  /**
   * Display a success toast message.
   *
   * @param message - Success message to display
   */
  const showSuccess = useCallback((message: string) => {
    toast.success(message, {
      duration: 4000,
    });
  }, []);

  /**
   * Display a warning toast message.
   *
   * @param message - Warning message to display
   */
  const showWarning = useCallback((message: string) => {
    toast.warning(message, {
      duration: 4500,
    });
  }, []);

  /**
   * Display an info toast message.
   *
   * @param message - Info message to display
   */
  const showInfo = useCallback((message: string) => {
    toast.info(message, {
      duration: 4000,
    });
  }, []);

  return {
    showError,
    showSuccess,
    showWarning,
    showInfo,
  };
}
